/**
 * Shared entity detail tabs component.
 * Renders Description, Memory, and dynamic linked-entity tabs.
 *
 * Used by contacts, companies, and projects detail views.
 * Each module can wrap this with module-specific header content.
 */

import { useState, useCallback, useRef, useMemo } from "react";
import type { JSX } from "react";
import {
  SearchableTabs,
  EntitySection,
  Icon,
  IconButton,
  Stack,
  Text,
} from "../../components/ui";
import { Scrollable } from "../../components/ui";
import { MarkdownEditor } from "../../components/shared/MarkdownEditor";
import { useEditorMentionSuggestion } from "../../components/shared/markdown/useEditorMentionSuggestion";
import { ExpandableEntityCard } from "../../components/agent/ExpandableEntityCard";
import type { LinkedEntitySummary } from "./sharedTypes";
import { computeDynamicTabs, buildAllTabs } from "./entityTabUtils";
import { useEntityProperty } from "./useEntityProperty";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { FilesTab } from "./FilesTab";

export interface EntityDetailTabsProps {
  readonly entityId: string;
  readonly linkedEntities: readonly LinkedEntitySummary[];
  /** Max visible tabs before overflow "..." (default 7) */
  readonly maxVisibleTabs?: number;
  /** Enable search mode in tabs */
  readonly searchable?: boolean;
  /** Content to render above the tabs (e.g., InfoCard for companies) */
  readonly headerContent?: React.ReactNode;
  /** When provided, a "Details" tab is prepended (before Description /
   *  Memory / dynamic) and becomes the default active tab. Contacts
   *  use this for the Google-Contacts-style contact-info column. */
  readonly detailsContent?: React.ReactNode;
}

export function EntityDetailTabs({
  entityId,
  linkedEntities,
  maxVisibleTabs = 7,
  searchable = false,
  headerContent,
  detailsContent,
}: EntityDetailTabsProps): JSX.Element {
  const runtime = useAppRuntime();
  const hasDetailsTab = detailsContent !== undefined && detailsContent !== null;
  const [activeTab, setActiveTab] = useState(hasDetailsTab ? "details" : "description");

  // Build module title map from runtime registry
  const moduleTitles = useMemo(() => {
    const map: Record<string, string> = {};
    for (const mod of runtime.modules.list()) {
      map[mod.id] = mod.title;
    }
    return map;
  }, [runtime.modules]);

  const dynamicTabs = computeDynamicTabs(linkedEntities, moduleTitles);
  const baseTabs = buildAllTabs(dynamicTabs);
  // When the consumer provides Overview content, the Description
  // description is rendered inside Overview's right column — drop the
  // standalone Description tab to avoid duplication.
  const fixedFiltered = hasDetailsTab
    ? baseTabs.filter((t) => t.id !== "description")
    : baseTabs;

  // No triggers tab is built here. A trigger arrives like any other linked
  // entity — contacts return what hangs off their replicas — so the generic
  // dynamic tab lists it and the triggers plugin's own card draws it. The host
  // used to discover triggers by RPC and add a second tab with the same id,
  // which rendered both bodies at once.

  // Files tab is always present so the operator can drop a file even
  // when nothing's attached yet. Inside, the dropzone shows above the
  // (possibly empty) list of currently-linked `file.object` entities.
  const filesTab = [{ id: "files", label: "Files" }];

  const allTabs = hasDetailsTab
    ? [{ id: "details", label: "Overview" }, ...fixedFiltered, ...filesTab]
    : [...fixedFiltered, ...filesTab];

  const attachedFiles = useMemo(
    () => linkedEntities.filter((e) => e.schema_id === "file.object"),
    [linkedEntities],
  );

  // S5: the description is a KEY of the node's dictionary — every fold
  // mapped its family's description to it — so the editor reads and
  // writes the node.
  const description = useEntityProperty(entityId, "description");

  // S6: agent memory is a key of the node's dictionary, like the description.
  // It is deliberately NOT `memory`: the contacts fold gave that key the
  // hub's curated memory ENTRIES (title + body per claim), and one word may
  // not mean two shapes.
  const memory = useEntityProperty(entityId, "agent_memory");

  const handleDescriptionChange = useCallback(
    (markdown: string) => {
      description.save(markdown);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [description.save],
  );

  const handleMemoryChange = useCallback(
    (markdown: string) => {
      memory.save(markdown);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memory.save],
  );

  return (
    <Scrollable>
      {headerContent ?? null}

      <SearchableTabs
        tabs={allTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        searchable={searchable}
        maxVisibleTabs={maxVisibleTabs}
      />

      {/* ── Tab Content ─────────────────────────────────────── */}
      <Stack gap={5}>
        {activeTab === "details" && hasDetailsTab ? (
          <div className="px-5 py-4">{detailsContent}</div>
        ) : null}

        {activeTab === "description" ? (
          <DescriptionTab
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            body={description.value}
            isLoading={description.isLoading}
            onChange={handleDescriptionChange}
          />
        ) : null}

        {activeTab === "memory" ? (
          <DescriptionTab
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            body={memory.value}
            isLoading={memory.isLoading}
            onChange={handleMemoryChange}
            placeholder="Agent memory..."
          />
        ) : null}

        {/* Dynamic tabs — linked entity lists */}
        {dynamicTabs.map((tab) =>
          activeTab === tab.id ? (
            <DynamicTabContent
              key={tab.id}
              tab={tab}
              linkedEntities={linkedEntities}
            />
          ) : null,
        )}

        {activeTab === "files" ? (
          <FilesTab entityId={entityId} attachedFiles={attachedFiles} />
        ) : null}
      </Stack>
    </Scrollable>
  );
}

/* ── Description Tab ──────────────────────────────────────── */

function DescriptionTab({
  body,
  isLoading,
  onChange,
  placeholder = "Add a description...",
}: {
  readonly body: string;
  readonly isLoading: boolean;
  readonly onChange: (markdown: string) => void;
  readonly placeholder?: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const localRef = useRef(body);
  localRef.current = body;
  const mentionSuggestion = useEditorMentionSuggestion();

  const handleToggleEdit = useCallback(() => {
    setEditing((m) => {
      // Remount the editor when we toggle so the fresh body becomes
      // the next initialValue (no stale content after save).
      setEditorKey((k) => k + 1);
      return !m;
    });
  }, []);

  if (isLoading) {
    return (
      <Stack gap={3} align="center" className="py-12">
        <Text variant="body" color="tertiary">Loading...</Text>
      </Stack>
    );
  }

  const isEmpty = !body.trim();
  // Same DOM layout in view and edit modes — only `readOnly` flips
  // and the toggle icon swaps pencil ↔ check. No toolbar bar above,
  // no padding jump. Click pencil → cursor activates in the same
  // spot the read-only text was rendered.
  const editorClass = "[&_.ProseMirror]:!p-0 [&_.milkdown-editor-wrapper]:!p-0";

  return (
    <div className="px-5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {isEmpty && !editing ? (
            <div
              className="text-content-tertiary text-sm cursor-pointer hover:text-content-secondary transition-colors"
              onClick={handleToggleEdit}
            >
              {placeholder}
            </div>
          ) : (
            <MarkdownEditor
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              key={`${editing ? "edit" : "view"}-${editorKey}`}
              initialValue={body}
              onChange={editing ? onChange : (): void => { /* read-only */ }}
              placeholder={placeholder}
              readOnly={!editing}
              autoFocus={editing}
              mentionSuggestion={editing ? mentionSuggestion : undefined}
              className={editorClass}
            />
          )}
        </div>
        <IconButton variant="ghost" onClick={handleToggleEdit} label={editing ? "Done" : "Edit"}>
          <Icon name={editing ? "check" : "pencil"} size={14} />
        </IconButton>
      </div>
    </div>
  );
}

/* ── Dynamic Tab Content ──────────────────────────────────── */

function DynamicTabContent({
  tab,
  linkedEntities,
}: {
  readonly tab: { readonly id: string; readonly label: string; readonly icon: string; readonly entityIds: readonly string[]; readonly groups?: readonly { readonly schemaId: string; readonly label: string; readonly icon: string; readonly entityIds: readonly string[] }[] };
  readonly linkedEntities: readonly LinkedEntitySummary[];
}): JSX.Element {
  const runtime = useAppRuntime();

  // If tab has entity type groups (subheadings), render them; otherwise flat list
  const groups = tab.groups && tab.groups.length > 0
    ? tab.groups
    : [{ schemaId: "", label: tab.label, icon: tab.icon, entityIds: tab.entityIds }];

  return (
    <Stack gap={4} className="px-5 py-4">
      {groups.map((group) => {
        const groupEntities = linkedEntities.filter((e) => group.entityIds.includes(e.id));

        // Deduplicate
        const seenNames = new Set<string>();
        const seenIds = new Set<string>();
        const deduped = groupEntities.filter((e) => {
          if (seenIds.has(e.id)) return false;
          seenIds.add(e.id);
          if (!e.name) return true;
          if (seenNames.has(e.name)) return false;
          seenNames.add(e.name);
          return true;
        });

        if (deduped.length === 0) return null;

        return (
          <EntitySection
            key={group.schemaId || group.label}
            icon={<Icon name={group.icon as Parameters<typeof Icon>[0]["name"]} size={18} className="text-content-secondary" />}
            title={group.label}
            count={groupEntities.length}
            countColor="blue"
          >
            <Stack gap={2}>
              {deduped.map((entity) => (
                <ExpandableEntityCard
                  key={entity.id}
                  schemaId={entity.schema_id}
                  data={{ id: entity.id, name: entity.name, ...entity.data }}
                  runtime={runtime}
                />
              ))}
            </Stack>
          </EntitySection>
        );
      })}
    </Stack>
  );
}
